import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { CATEGORY_KINDS, CATEGORY_KIND_LABELS, categoryKindOrOther } from '../../lib/portalAssets/categoryKinds';
import { crossClubRows, crossClubTotals, moveCategory } from '../../lib/portalAssets/staffView';
import { RETENTION_DAYS } from '../../lib/portalAssets/deletion';
import { listAccounts } from '../accounts';
import { StaffAllAssetsSection } from './StaffAllAssetsSection';
import {
  createAssetCategory,
  deleteAssetCategory,
  listAssetCategories,
  listBrandAssetCounts,
  editAssetCategory,
  reorderAssetCategories,
} from './staffApi';

// /tools/portal-assets — the STAFF cross-club view (spec §8, commit 13): pick a
// brand, see every club's asset counts most-pending first, open a club's Assets
// tab to review; and edit that brand's categories (C8: categories are PER BRAND).
//
// ⚠️ A club appears here only once it has a counts doc — i.e. once something
// was uploaded. A club that has uploaded nothing has nothing to review, so its
// absence is the right answer here, not a gap.

export function PortalAssetsToolPage() {
  const [params, setParams] = useSearchParams();
  const brandId = params.get('brand') ?? '';
  // Two views of the same surface: the per-brand counts + categories and
  // the cross-account list of every upload (the default; ?view=all still works).
  const showAll = params.get('view') !== 'by-club';
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const brands = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.accountType === 'brand')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts.data],
  );
  const names = useMemo(() => new Map((accounts.data ?? []).map((a) => [a.id, a.name])), [accounts.data]);

  const counts = useQuery({
    queryKey: ['portal-assets-staff', 'brand-counts', brandId],
    queryFn: () => listBrandAssetCounts(brandId),
    enabled: brandId !== '',
  });
  const rows = crossClubRows(counts.data?.counts ?? [], names);
  const totals = crossClubTotals(rows);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Portal assets"
        description="Photos and videos clubs have uploaded through the client portal, by brand. Open a club to review its uploads."
      />
      <div className="flex gap-2">
        <Button size="sm" variant={showAll ? 'outline' : 'default'} onClick={() => setParams({ view: 'by-club' }, { replace: true })}>
          Brand Settings
        </Button>
        <Button size="sm" variant={showAll ? 'default' : 'outline'} onClick={() => setParams({ view: 'all' }, { replace: true })}>
          All uploads
        </Button>
      </div>
      {showAll ? (
        <StaffAllAssetsSection />
      ) : (
        <>
      <div className="max-w-sm">
        <Combobox
          value={brandId}
          onValueChange={(v) => setParams(v ? { view: 'by-club', brand: v } : { view: 'by-club' }, { replace: true })}
          options={brands}
          placeholder={accounts.isPending ? 'Loading brands…' : 'Choose a brand'}
          searchPlaceholder="Search brands…"
          aria-label="Brand"
        />
      </div>

      {brandId === '' ? (
        <p className="text-sm text-muted-foreground">Choose a brand to see its clubs.</p>
      ) : counts.isPending ? (
        <CenteredSpinner />
      ) : counts.isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load this brand's asset counts.</p>
      ) : (
        <section className="space-y-3">
          <p className="text-sm text-foreground">
            {rows.length === 0
              ? 'No club in this brand has uploaded anything yet.'
              : `${totals.pending} waiting for review across ${totals.clubsWaiting} club${totals.clubsWaiting === 1 ? '' : 's'}.`}
          </p>
          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Club</th>
                    <th className="px-3 py-2 text-right font-medium">Waiting</th>
                    <th className="px-3 py-2 text-right font-medium">Approved</th>
                    <th className="px-3 py-2 text-right font-medium">Not approved</th>
                    <th className="px-3 py-2 text-right font-medium">Processing</th>
                    <th className="px-3 py-2 text-right font-medium">Failed</th>
                    <th className="px-3 py-2 text-right font-medium" title="Videos stored and waiting for the video Job to process them">Videos queued</th>
                    <th className="px-3 py-2 text-right font-medium" title={`Deleted and not yet purged: Kyck can restore these for ${RETENTION_DAYS} days, after which they are purged. Purged assets are not counted.`}>Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.accountId} className="border-t border-border">
                      <td className="px-3 py-2">
                        {r.known ? (
                          <Link to={`/accounts/${encodeURIComponent(r.accountId)}?tab=assets`} className="text-foreground underline">
                            {r.name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">{r.name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{r.pending}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.approved}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.rejected}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.processing}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.failed}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.awaitingSupport}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid="deleted-count">{r.deleted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {brandId !== '' && <CategoryEditor brandAccountId={brandId} />}
        </>
      )}
    </div>
  );
}

/** The brand's asset categories: add, edit, reorder, delete (refused while in use). */
export function CategoryEditor({ brandAccountId }: { brandAccountId: string }) {
  const qc = useQueryClient();
  const key = ['portal-assets-staff', 'categories', brandAccountId];
  const list = useQuery({ queryKey: key, queryFn: () => listAssetCategories(brandAccountId) });
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<string>('');
  const [editing, setEditing] = useState<{ id: string; name: string; description: string } | null>(null);
  const done = () => void qc.invalidateQueries({ queryKey: key });
  const create = useMutation({ mutationFn: (v: { name: string; kind: string }) => createAssetCategory(brandAccountId, v.name, v.kind), onSuccess: () => { setNewName(''); setNewKind(''); done(); } });
  const rename = useMutation({ mutationFn: (e: { id: string; name: string; description: string }) => editAssetCategory(e.id, { name: e.name, description: e.description }), onSuccess: () => { setEditing(null); done(); } });
  const reorder = useMutation({ mutationFn: (ids: string[]) => reorderAssetCategories(brandAccountId, ids), onSuccess: done });
  const remove = useMutation({ mutationFn: (id: string) => deleteAssetCategory(id), onSuccess: done });
  // ⚠️ AN ERROR BELONGS TO THE ATTEMPT THAT CAUSED IT. The four mutations share one message line, and nothing used to
  // reset them, so a failed add's (correct, useful) 409 outlived the input it was about and sat through unrelated edits.
  // The message is not hidden: its LIFETIME is bounded. It clears when the user changes what it relates to (below),
  // and when ANOTHER action starts (resetOthers); a mutation's own error clears when it is retried or succeeds.
  const resetOthers = (keep: 'create' | 'rename' | 'reorder' | 'remove') => {
    if (keep !== 'create') create.reset();
    if (keep !== 'rename') rename.reset();
    if (keep !== 'reorder') reorder.reset();
    if (keep !== 'remove') remove.reset();
  };
  const error = create.error ?? rename.error ?? reorder.error ?? remove.error;
  const busy = create.isPending || rename.isPending || reorder.isPending || remove.isPending;
  const cats = list.data?.categories ?? [];
  const ids = cats.map((c) => c.id);

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">Categories for this brand</h2>
      <p className="max-w-prose text-sm text-muted-foreground">
        Clubs see these in this order. A category in use can’t be deleted — move its assets first. The type
        (Team, Equipment…) is what lets the agency find the same idea across brands, and it can’t be changed
        once the category exists. The description is for the person filing by hand and for the AI: say what belongs, then what does NOT.
      </p>
      {list.isPending ? (
        <CenteredSpinner />
      ) : (
        <ul className="max-w-xl divide-y divide-border rounded-md border border-border">
          {cats.length === 0 && <li className="p-3 text-sm text-muted-foreground">No categories yet.</li>}
          {cats.map((c, i) => (
            <li key={c.id} className="flex items-center gap-2 p-2 text-sm">
              {editing?.id === c.id ? (
                <form
                  className="flex flex-1 flex-wrap gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (editing.name.trim()) resetOthers('rename');
                    if (editing.name.trim()) rename.mutate({ id: c.id, name: editing.name.trim(), description: editing.description.trim() });
                  }}
                >
                  <Input className="flex-1" value={editing.name} onChange={(e) => { rename.reset(); setEditing({ ...editing, name: e.target.value }); }} aria-label="Category name" />
                  <textarea
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                    rows={3}
                    maxLength={300}
                    value={editing.description}
                    onChange={(e) => { rename.reset(); setEditing({ ...editing, description: e.target.value }); }}
                    placeholder="What belongs here — then NOT what doesn’t. e.g. “The premises from outside: facade, signage, carpark. NOT a nearby landmark.”"
                    aria-label="Category description"
                    data-testid="category-description-input"
                  />
                  <Button size="sm" type="submit" disabled={busy}>Save</Button>
                  <Button size="sm" type="button" variant="ghost" onClick={() => { rename.reset(); setEditing(null); }}>Cancel</Button>
                </form>
              ) : (
                <>
                  <span className="flex-1 text-foreground">
                    {c.name}
                    {c.description !== '' && <span className="block text-xs text-muted-foreground" data-testid="category-description">{c.description}</span>}
                  </span>
                  {/* ⚠️ The type is shown, never editable: it is fixed when the category is created. */}
                  <span className="text-xs text-muted-foreground" data-testid="category-kind">{CATEGORY_KIND_LABELS[categoryKindOrOther(c.kind)]}</span>
                  <span className="text-xs text-muted-foreground">{c.assetCount} in use</span>
                  <Button size="sm" variant="ghost" disabled={busy || i === 0} aria-label={`Move ${c.name} up`} onClick={() => { resetOthers('reorder'); reorder.mutate(moveCategory(ids, i, -1)); }}>↑</Button>
                  <Button size="sm" variant="ghost" disabled={busy || i === cats.length - 1} aria-label={`Move ${c.name} down`} onClick={() => { resetOthers('reorder'); reorder.mutate(moveCategory(ids, i, 1)); }}>↓</Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => { resetOthers('rename'); rename.reset(); setEditing({ id: c.id, name: c.name, description: c.description }); }}>Edit</Button>
                  <Button size="sm" variant="ghost" disabled={busy || c.assetCount > 0} onClick={() => { resetOthers('remove'); remove.mutate(c.id); }}>Delete</Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex max-w-xl gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (newName.trim() && newKind) resetOthers('create');
          if (newName.trim() && newKind) create.mutate({ name: newName.trim(), kind: newKind });
        }}
      >
        <Input value={newName} onChange={(e) => { create.reset(); setNewName(e.target.value); }} placeholder="New category" aria-label="New category name" />
        <div className="w-44">
          <SelectField
            value={newKind}
            onValueChange={(v) => { create.reset(); setNewKind(v); }}
            options={[{ value: '', label: 'Type…' }, ...CATEGORY_KINDS.map((k) => ({ value: k, label: CATEGORY_KIND_LABELS[k] }))]}
            aria-label="New category type"
          />
        </div>
        <Button type="submit" disabled={busy || newName.trim() === '' || newKind === ''}>Add</Button>
      </form>
      {error && <p className="text-sm text-destructive" role="alert">{error.message}</p>}
    </section>
  );
}
